import { useLocation, useNavigate } from "react-router-dom";
import type { NavItem } from "@/lib/navigation";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";
import { Badge } from "@/components/ui/badge";

export function NavMain({
  items,
  label,
  labelClassName,
}: {
  items: NavItem[];
  label?: string;
  labelClassName?: string;
}) {
  const location = useLocation();
  const navigate = useNavigate();

  return (
    <SidebarGroup>
      {label ? (
        <SidebarGroupLabel className="text-xs font-semibold tracking-wider text-muted-foreground/70 uppercase">
          {label}
        </SidebarGroupLabel>
      ) : null}
      <SidebarGroupContent className="flex flex-col gap-2">
        <SidebarMenu>
          {items.map((item) => {
            const isActive = (() => {
              const [itemPath, itemQuery] = item.url.split("?");
              const currentParams = new URLSearchParams(location.search);

              if (itemQuery) {
                // Item has query params — require exact pathname AND all query params to match
                const itemParams = new URLSearchParams(itemQuery);
                if (location.pathname !== itemPath) return false;
                for (const [key, value] of itemParams) {
                  if (currentParams.get(key) !== value) return false;
                }
                return true;
              }

              // Item has no query — match pathname prefix
              const pathMatches = location.pathname === itemPath || location.pathname.startsWith(itemPath + "/");
              if (!pathMatches) return false;

              // If a sibling item (same list, same pathname) has a matching tab param, let the sibling win
              if (location.pathname === itemPath) {
                const currentTab = currentParams.get("tab");
                if (currentTab) {
                  const siblingOwnsTab = items.some((other) => {
                    if (other.url === item.url) return false;
                    const [siblingPath, siblingQuery] = other.url.split("?");
                    if (siblingPath !== itemPath || !siblingQuery) return false;
                    const siblingParams = new URLSearchParams(siblingQuery);
                    return siblingParams.get("tab") === currentTab;
                  });
                  if (siblingOwnsTab) return false;
                }
              }

              return true;
            })();
            const Icon = item.icon;
            return (
              <SidebarMenuItem key={item.title} className="relative">
                <SidebarMenuButton
                  isActive={isActive}
                  onClick={() => navigate(item.url)}
                  title={item.title}
                >
                  {Icon && <Icon />}
                  <span className={labelClassName}>{item.title}</span>
                </SidebarMenuButton>
                {item.badge && item.badge > 0 ? (
                  <span
                    aria-label={`${item.badge} unread`}
                    className="pointer-events-none absolute top-1/2 right-2 -translate-y-1/2 flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-[10px] font-bold text-white shadow-sm tabular-nums select-none"
                  >
                    {item.badge > 99 ? "99+" : item.badge}
                  </span>
                ) : null}
                {item.action}
              </SidebarMenuItem>
            );
          })}
        </SidebarMenu>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
